import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { initialState } from "@/lib/storage";
import { upsertRegistration, toBackendPayload, uploadFile, dataUrlToBase64, getRegistration, syncPolicySignStatus, getCandidateToken, clearCandidateToken, markNeedsOtpReauth } from "@/lib/api";
import { POLICY_ITEMS, getRegistrationSteps, normalizeStoredStep } from "@/lib/constants";

const RegistrationContext = createContext(null);

const POLICY_KEYS = ["antiDrugAnnexure", "antiDrugPolicy", "highRiskConsent"];

const mergeRemotePolicy = (local, remote) => {
  if (!remote) return local;
  return { ...(local || {}), ...remote };
};

const mergeRemotePolicies = (local = {}, remote = {}) => {
  const out = { ...local };
  const keys = new Set([...Object.keys(local), ...Object.keys(remote)]);
  keys.forEach((k) => {
    out[k] = mergeRemotePolicy(local[k], remote[k]);
  });
  return out;
};

export const RegistrationProvider = ({ children }) => {
  const [state, setState] = useState(() => initialState());
  const [saveStatus, setSaveStatus] = useState("idle");
  // Reactive mirror of hydratedFor — lets consumers (e.g. Success.jsx) wait for
  // the backend fetch to finish before deciding to redirect away, instead of
  // judging off the still-default initialState() and bouncing prematurely.
  const [hydrated, setHydrated] = useState(false);
  const syncTimerRef = useRef(null);
  const uploadInFlight = useRef(new Set());
  // hydratedFor holds the application_number we've already pulled from the backend —
  // gates the debounced sync so it never flushes before the DB fetch completes.
  const hydratedFor = useRef(null);

  // Debounced backend sync — Supabase is the source of truth.
  // Guard: never flush until hydration for the current app_no has finished,
  // otherwise an empty initialState() would overwrite good DB data.
  useEffect(() => {
    if (!state.applicationNumber) return;
    if (!getCandidateToken()) return;
    if (hydratedFor.current !== state.applicationNumber) return;
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    setSaveStatus("saving");
    syncTimerRef.current = setTimeout(async () => {
      await upsertRegistration(toBackendPayload(state));
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 1200);
    }, 800);
    return () => syncTimerRef.current && clearTimeout(syncTimerRef.current);
  }, [state]);

  // Fetch persisted state from backend when applicationNumber becomes known
  // AND the candidate has an OTP session token (never call full GET pre-login).
  useEffect(() => {
    const appNo = state.applicationNumber;
    if (!appNo || hydratedFor.current === appNo) return;
    if (!getCandidateToken()) return;

    // Only wipe in-memory state when switching to a *different* application in the
    // same tab. On a hard refresh hydratedFor is null — skip the reset so we never
    // flash empty fields or the wrong wizard step before the DB fetch completes.
    const switchingApp = hydratedFor.current != null && hydratedFor.current !== appNo;
    if (switchingApp) {
      hydratedFor.current = null;
      setHydrated(false);
      setState((s) => ({
        ...initialState(),
        applicationNumber: appNo,
        mobile: s.mobile,
        qid: s.qid,
        otpVerified: s.otpVerified,
      }));
    }

    (async () => {
      const remote = await getRegistration(appNo);
      if (!remote) {
        // 401 / missing: if we lost the session, clear token so Landing re-auth works.
        if (!getCandidateToken()) {
          clearCandidateToken();
          markNeedsOtpReauth();
        }
        // Nothing on the backend yet — this is a brand-new candidate. Mark hydrated
        // anyway so the debounced sync can start persisting their fresh input.
        hydratedFor.current = appNo;
        setHydrated(true);
        return;
      }
      // Rebuild documents from registration_files (preserve file_url so previews render)
      const documents = {};
      Object.entries(remote.documents || {}).forEach(([k, files]) => {
        documents[k] = (files || []).map((f) => ({
          name: f.name, size: f.size, type: f.content_type, file_url: f.file_url, file_path: f.file_path, uploaded: true, status: f.status,
        }));
      });
      const policies = mergeRemotePolicies({}, remote.policies || {});
      const photograph = remote.photograph;
      // Map backend snake_case candidate fields → frontend camelCase.
      const c = remote.candidate || {};
      const remoteCandidate = {
        ...c,
        // Explicit remaps (keep original keys around too so upstream code that
        // already reads snake_case still works).
        parent1Name: c.parent1_name ?? c.parent1Name ?? "",
        parent1Phone: c.parent1_phone ?? c.parent1Phone ?? "",
        parent1Relation: c.parent1_relation ?? c.parent1Relation ?? "",
        parent1Email: c.parent1_email ?? c.parent1Email ?? "",
        parent2Name: c.parent2_name ?? c.parent2Name ?? "",
        parent2Phone: c.parent2_phone ?? c.parent2Phone ?? "",
        parent2Relation: c.parent2_relation ?? c.parent2Relation ?? "",
        parent2Email: c.parent2_email ?? c.parent2Email ?? "",
        guardianName: c.guardian_name ?? c.guardianName ?? "",
        guardianPhone: c.guardian_phone ?? c.guardianPhone ?? "",
        guardianRelation: c.guardian_relation ?? c.guardianRelation ?? "",
        dateOfBirth: c.date_of_birth ?? c.dateOfBirth ?? "",
        educationBoard: c.education_board ?? c.educationBoard ?? "",
        bloodGroup: c.blood_group ?? c.bloodGroup ?? "",
        affidavitRef: c.affidavit_ref ?? c.affidavitRef ?? "",
        emergencyContactName: c.emergency_contact_name ?? c.emergencyContactName ?? "",
        emergencyContactPhone: c.emergency_contact_phone ?? c.emergencyContactPhone ?? "",
        emergencyContactRelation: c.emergency_contact_relation ?? c.emergencyContactRelation ?? "",
        fullAddress: c.full_address ?? c.fullAddress ?? "",
        accuracyConfirmed: c.accuracy_confirmed ?? c.accuracyConfirmed ?? false,
        isFromHyderabad: c.is_from_hyderabad ?? c.isFromHyderabad ?? null,
        isHosteller: c.is_hosteller ?? c.isHosteller ?? null,
      };
      setState((s) => ({
        ...s,
        applicationNumber: appNo,
        // The DB mobile is the OTP-verified one (otp/verify binds it before the
        // session token exists, and this only runs with a token), so it wins
        // over whatever this browser cached — on a shared computer the cached
        // value can be a different student's number.
        mobile: remote.mobile || s.mobile,
        qid: remote.qid || s.qid,
        otpVerified: !!remote.otp_verified || s.otpVerified,
        assignedSlot: remote.assigned_slot || s.assignedSlot,
        ackNumber: remote.ack_number || s.ackNumber,
        submitted: !!remote.submitted,
        submittedAt: remote.submitted_at || s.submittedAt,
        status: remote.status || s.status,
        docStatuses: remote.doc_statuses || {},
        emailVerified: remote.email_verified || {},
        // Stored inside candidate (not a sibling column, unlike email_verified) —
        // avoided a schema migration by piggybacking on the existing JSONB blob.
        phoneVerified: { parent1: !!c.parent1_phone_verified, parent2: !!c.parent2_phone_verified },
        // Merge course info — keep Landing verify seed if the remote row has
        // blank program fields (common before category links are fully set).
        courseInfo: (() => {
          const remoteCourse = remote.course_info || {};
          const localCourse = s.courseInfo || {};
          return {
            program: remoteCourse.program || localCourse.program || null,
            specialization: remoteCourse.specialization || localCourse.specialization || null,
            school: remoteCourse.school || localCourse.school || null,
            display: remoteCourse.display || localCourse.display || null,
            branch: remoteCourse.branch || localCourse.branch || null,
          };
        })(),
        skipPolicies: !!remote.skip_policies || s.skipPolicies,
        originalReceived: remote.original_received || {},
        lastActionReason: remote.last_action_reason || null,
        history: remote.history || [],
        step: normalizeStoredStep(remote.step || s.step, !!remote.skip_policies || s.skipPolicies),
        policiesFilled: remote.policies_filled || s.policiesFilled || {},
        candidate: {
          ...s.candidate,
          ...remoteCandidate,
          photo: photograph
            ? { name: photograph.name, file_url: photograph.file_url, file_path: photograph.file_path, uploaded: true }
            : s.candidate.photo,
        },
        documents: { ...s.documents, ...documents },
        policies: { ...s.policies, ...policies },
        undertaking: {
          confirmed: !!remote.undertaking_confirmed || s.undertaking?.confirmed,
          signedAt: remote.undertaking_signed_at || s.undertaking?.signedAt,
        },
      }));
      // Mark hydration complete — the debounced sync is now allowed to persist changes.
      hydratedFor.current = appNo;
      setHydrated(true);
    })();
  }, [state.applicationNumber, state.otpVerified]);

  // Background uploader — files with dataUrl but no file_url are pushed to Supabase Storage.
  useEffect(() => {
    const appNo = state.applicationNumber;
    if (!appNo) return;

    const photo = state.candidate?.photo;
    if (photo?.dataUrl && !photo.file_url && !photo.uploading) {
      const tag = `photo::${photo.name}`;
      if (!uploadInFlight.current.has(tag)) {
        uploadInFlight.current.add(tag);
        setState((s) => ({ ...s, candidate: { ...s.candidate, photo: { ...s.candidate.photo, uploading: true } } }));
        uploadFile({ applicationNumber: appNo, category: "photograph", key: "photograph", name: photo.name, contentType: photo.type || "image/jpeg", size: photo.size, dataBase64: dataUrlToBase64(photo.dataUrl) })
          .then((res) => {
            uploadInFlight.current.delete(tag);
            setState((s) => ({
              ...s,
              candidate: {
                ...s.candidate,
                photo: res?.file_url
                  ? { ...s.candidate.photo, file_url: res.file_url, file_path: res.file_path, uploading: false, uploaded: true }
                  : { ...s.candidate.photo, uploading: false },
              },
              // Mirror the backend's auto-clear (same as documents/policies) so the
              // "reupload needed" badge disappears immediately after a fresh photo lands.
              docStatuses: res?.file_url && s.docStatuses?.photograph === "reupload"
                ? { ...s.docStatuses, photograph: "resubmitted" }
                : s.docStatuses,
            }));
          });
      }
    }

    Object.entries(state.documents || {}).forEach(([docKey, files]) => {
      (files || []).forEach((f, idx) => {
        if (f?.dataUrl && !f.file_url && !f.uploading) {
          const tag = `doc::${docKey}::${f.name}::${idx}`;
          if (uploadInFlight.current.has(tag)) return;
          uploadInFlight.current.add(tag);
          setState((s) => {
            const cur = (s.documents?.[docKey] || []).slice();
            if (!cur[idx]) return s;
            cur[idx] = { ...cur[idx], uploading: true };
            return { ...s, documents: { ...s.documents, [docKey]: cur } };
          });
          uploadFile({ applicationNumber: appNo, category: "document", key: docKey, name: f.name, contentType: f.type || "application/octet-stream", size: f.size, dataBase64: dataUrlToBase64(f.dataUrl) })
            .then((res) => {
              uploadInFlight.current.delete(tag);
              setState((s) => {
                const cur = (s.documents?.[docKey] || []).slice();
                if (!cur[idx]) return s;
                cur[idx] = res?.file_url
                  ? { ...cur[idx], file_url: res.file_url, file_path: res.file_path, uploading: false, uploaded: true }
                  : { ...cur[idx], uploading: false };
                // Mirror the backend's auto-clear so the "reupload needed" badge
                // disappears immediately instead of waiting for a page refresh.
                const docStatuses = res?.file_url && s.docStatuses?.[docKey] === "reupload"
                  ? { ...s.docStatuses, [docKey]: "resubmitted" }
                  : s.docStatuses;
                return { ...s, documents: { ...s.documents, [docKey]: cur }, docStatuses };
              });
            });
        }
      });
    });

  }, [state.applicationNumber, state.candidate?.photo, state.documents, state.policies]);

  // Policies have no file upload — "Send for Sign" just sets a `sent` flag
  // directly (synchronous, no Storage round-trip). Mirror the same
  // auto-clear the documents/policy-file flow used to do: once a flagged
  // policy is sent again, immediately clear its "reupload needed" badge
  // instead of waiting for a page refresh.
  useEffect(() => {
    POLICY_KEYS.forEach((pk) => {
      if (state.policies?.[pk]?.sent && state.docStatuses?.[pk] === "reupload") {
        setState((s) => (s.docStatuses?.[pk] === "reupload"
          ? { ...s, docStatuses: { ...s.docStatuses, [pk]: "resubmitted" } }
          : s));
      }
    });
  }, [state.policies, state.docStatuses]);

  // Wizard-wide Leegality sign-status polling — lives here (not in PolicyBuilder,
  // which only exists while the candidate is on the Forms & Policies step) so it
  // keeps running no matter which step they've moved on to. Without this, a
  // candidate who sends a policy for signing and then proceeds to Undertaking /
  // Review & Submit (the natural place to wait) would never see the status
  // update — polling used to die the moment PolicyBuilder unmounted.
  const signedToastShown = useRef(new Set());
  useEffect(() => {
    const appNo = state.applicationNumber;
    if (!appNo || hydratedFor.current !== appNo || state.submitted) return undefined;
    if (state.skipPolicies) return undefined;

    const pendingIds = POLICY_ITEMS
      .filter((item) => !item.viewOnly)
      .map((item) => item.id)
      .filter((id) => {
        const p = state.policies?.[id];
        const signStatus = p?.signStatus || (p?.sent ? "pending" : undefined);
        return signStatus === "pending" && state.docStatuses?.[id] !== "reupload";
      });
    if (pendingIds.length === 0) return undefined;

    let cancelled = false;
    const poll = async () => {
      for (const id of pendingIds) {
        const { ok, data } = await syncPolicySignStatus(appNo, id);
        if (cancelled || !ok || data?.signStatus !== "signed") continue;
        setState((s) => ({
          ...s,
          policies: {
            ...s.policies,
            [id]: {
              ...(s.policies?.[id] || {}),
              signStatus: "signed",
              signedAt: data.signedAt || new Date().toISOString(),
              leegalityDocumentId: data.documentId || s.policies?.[id]?.leegalityDocumentId,
            },
          },
        }));
        if (!signedToastShown.current.has(id)) {
          signedToastShown.current.add(id);
          const label = POLICY_ITEMS.find((item) => item.id === id)?.label || id;
          toast.success(`${label} signed successfully`);
        }
      }
    };

    poll();
    const iv = setInterval(poll, 15000);
    return () => {
      cancelled = true;
      clearInterval(iv);
    };
  }, [state.applicationNumber, state.submitted, state.policies, state.docStatuses, state.skipPolicies]);

  const update = (partial) => setState((s) => ({ ...s, ...partial }));
  const updateCandidate = (partial) =>
    setState((s) => ({ ...s, candidate: { ...s.candidate, ...partial } }));
  const setDocuments = (docId, files) =>
    setState((s) => ({ ...s, documents: { ...s.documents, [docId]: files } }));
  const resetAll = () => setState(initialState());

  // Re-pull admin-controlled fields (doc_statuses, documents, policies, status,
  // history) from the backend. The initial hydration effect above only runs once
  // per session (`hydratedFor` gate) — without this, an admin reupload request
  // (which flags doc_statuses and deletes the file server-side) never becomes
  // visible to a candidate whose tab was already open; they'd need a hard reload.
  const refreshDocStatus = async () => {
    const appNo = state.applicationNumber;
    if (!appNo || hydratedFor.current !== appNo) return;
    const remote = await getRegistration(appNo);
    if (!remote) return;
    setState((s) => {
      // Never clobber a file that's actively mid-upload in this tab.
      const isUploading = (entry) => entry?.uploading;

      const documents = { ...s.documents };
      const docKeys = new Set([...Object.keys(remote.documents || {}), ...Object.keys(s.documents || {})]);
      docKeys.forEach((k) => {
        if ((s.documents?.[k] || []).some(isUploading)) return;
        const files = remote.documents?.[k] || [];
        documents[k] = files.map((f) => ({
          name: f.name, size: f.size, type: f.content_type, file_url: f.file_url, file_path: f.file_path, uploaded: true, status: f.status,
        }));
      });

      const policies = mergeRemotePolicies(s.policies, remote.policies || {});

      // Photo flagged for reupload → backend already deleted the file; clear it
      // locally too (unless a fresh one is actively mid-upload in this tab) so the
      // dropzone reappears instead of showing the now-dead image.
      const photoFlagged = remote.doc_statuses?.photograph === "reupload";
      const candidate = photoFlagged && !s.candidate?.photo?.uploading
        ? { ...s.candidate, photo: null }
        : s.candidate;

      return {
        ...s,
        status: remote.status || s.status,
        docStatuses: remote.doc_statuses || {},
        originalReceived: remote.original_received || {},
        lastActionReason: remote.last_action_reason || null,
        history: remote.history || [],
        documents,
        policies,
        candidate,
      };
    });
  };

  // Immediate (non-debounced) candidate-field save — used by the admin-requested
  // name/parent-name correction flow, where the candidate expects an explicit Save
  // action rather than the usual silent autosave-on-keystroke.
  const saveCandidateNow = async (partial) => {
    const appNo = state.applicationNumber;
    if (!appNo) return null;
    const nextCandidate = { ...state.candidate, ...partial };
    const res = await upsertRegistration(toBackendPayload({ ...state, candidate: nextCandidate }));
    setState((s) => {
      let docStatuses = s.docStatuses;
      Object.keys(partial).forEach((k) => {
        if (docStatuses?.[k] === "reupload") {
          docStatuses = { ...docStatuses, [k]: "resubmitted" };
        }
      });
      return { ...s, candidate: { ...s.candidate, ...partial }, docStatuses };
    });
    return res;
  };

  return (
    <RegistrationContext.Provider
      value={{ state, setState, update, updateCandidate, setDocuments, saveStatus, resetAll, refreshDocStatus, saveCandidateNow, hydrated }}
    >
      {children}
    </RegistrationContext.Provider>
  );
};

export const useRegistration = () => {
  const ctx = useContext(RegistrationContext);
  if (!ctx) throw new Error("useRegistration must be used within RegistrationProvider");
  return ctx;
};
